import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";

import { stakeholdersDatatableColumns } from "src/core/components/stakeholders-datatable/stakeholdersDatatableColumns";
import { MappedStakeholder, StakeholderRoleEnum } from "src/core/constants/folder";
import { useDocumentPreview } from "src/core/contexts/DocumentPreviewContext";
import { useFolder } from "src/core/contexts/FolderContext";
import { useFolderGraphSync } from "src/core/contexts/FolderGraphSyncContext";
import {
  useCallEReputationMutation,
  useCreateOrReplaceExternalDocumentMutation,
  useDeleteExternalDocumentMutation,
} from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { DocumentField } from "src/core/utils/documents";

export const useStakeholdersTable = () => {
  const [openStakeholderDocumentModal, setOpenStakeholderDocumentModal] = useState<boolean>(false);
  const [openDuplicateCheckModal, setOpenDuplicateCheckModal] = useState<boolean>(false);
  const [selectedStakeholderForDuplicateCheck, setSelectedStakeholderForDuplicateCheck] =
    useState<MappedStakeholder>();

  const {
    folderFile,
    mappedStakeholders,
    mappedEntities,
    isReadOnly,
    refetch,
    stakeholdersCounter,
    isPPH,
  } = useFolder();

  // console.log("mappedStakeholders", mappedStakeholders);

  const data = useMemo<MappedStakeholder[]>(() => mappedStakeholders, [mappedStakeholders]);

  // Est-ce que la liste des stakeholders possède un représentant légal ?
  const isLegalRepresentative = useMemo<boolean>(() => {
    const filtered = mappedStakeholders.filter((s) => !s.isFolderCustomer);
    return filtered.some((s) => s.roles.includes(StakeholderRoleEnum.legal_representative));
  }, [mappedStakeholders]);

  const [fileLoading, setFileLoading] = useState<string[]>([]);
  const [createOrReplaceExternalDocument] = useCreateOrReplaceExternalDocumentMutation();
  const [deleteExternalDocument] = useDeleteExternalDocumentMutation();
  const displayReloadButton = stakeholdersCounter.alerteNoAnalysis > 0;
  const [callEReputation] = useCallEReputationMutation();
  const { toast } = useToast();
  const [documentToUpload, setDocumentToUpload] = useState<DocumentField | null>(null);
  const {
    documentToPreview,
    openDocumentPreviewModal,
    handleOpenDocumentPreviewModal,
    handlePreviewDocument,
  } = useDocumentPreview();
  const {
    setOpenDeleteModal,
    setSelectedStakeholder,
    setSelectedStakeholderForEdit,
    setLoadingListStakeholders: setLoading,
    selectedStakeholderForEdit,
    loadingListStakeholders,
    setOpenAddModal,
    setOpenUpdateModal,
    setOpenEntityDetailsModal,
    openUpdateModal,
    setSelectedEntity,
    setIsFromBasicForm,
    setIsCentralSelected,
  } = useFolderGraphSync();

  /**
   * Permet d'ouvrir la modale de création d'un stakeholder basique.
   * On désélectionne tout ce qui pouvait être précédemment sélectionné.
   */
  const openModalAddBasicStakeholder = useCallback(() => {
    if (isPPH) {
      return;
    }
    setOpenAddModal(true);
    setIsCentralSelected(false);
    setSelectedStakeholder(undefined);
  }, [setOpenAddModal, setIsCentralSelected, setSelectedStakeholder, isPPH]);

  const handleDocument = useCallback(
    async (code: string, stakeholder: MappedStakeholder, label: string) => {
      setDocumentToUpload({ code, label });
      setSelectedStakeholderForEdit(stakeholder);
      setOpenStakeholderDocumentModal(true);
    },
    [setSelectedStakeholderForEdit]
  );

  const handleExternalDocumentChange = useCallback(
    async (
      checked: boolean,
      code: string,
      stakeholderId: string,
      documentId: string | null,
      inputId: string
    ) => {
      setFileLoading((prev) => [...prev, inputId]);
      if (checked) {
        createOrReplaceExternalDocument({
          variables: {
            entityId: stakeholderId,
            documentTypeCode: code,
          },
        }).then(async () => {
          await refetch();
          setFileLoading((prev) => prev.filter((id) => id !== inputId));
        });
      } else if (documentId) {
        deleteExternalDocument({
          variables: {
            documentId: documentId,
          },
        }).then(async () => {
          await refetch();
          setFileLoading((prev) => prev.filter((id) => id !== inputId));
        });
      }
    },
    [createOrReplaceExternalDocument, deleteExternalDocument, refetch]
  );

  const handleOpenDeleteModal = useCallback(
    (open: boolean, row?: MappedStakeholder) => {
      setOpenDeleteModal(open);
      setSelectedStakeholder(row);
    },
    [setOpenDeleteModal, setSelectedStakeholder]
  );

  const handleOpenEditModal = useCallback(
    (open: boolean, row?: MappedStakeholder) => {
      setOpenUpdateModal(open);
      setSelectedStakeholderForEdit(row);
      setIsFromBasicForm(false);
    },
    [setOpenUpdateModal, setSelectedStakeholderForEdit, setIsFromBasicForm]
  );

  const handleOpenEntityDetailsModal = useCallback(
    (open: boolean, row?: MappedStakeholder) => {
      const entity = mappedEntities.find((e) => e.id === row?.id);
      if (entity) {
        setSelectedEntity(entity);
        setOpenEntityDetailsModal(open);
      }
    },
    [mappedEntities, setSelectedEntity, setOpenEntityDetailsModal]
  );

  const handleOpenDuplicateCheckModal = useCallback((open: boolean, row?: MappedStakeholder) => {
    setOpenDuplicateCheckModal(open);
    if (!open) {
      setSelectedStakeholderForDuplicateCheck(undefined);
      return;
    }
    if (row) {
      setSelectedStakeholderForDuplicateCheck(row);
    }
  }, []);

  // Permet de gérer les colonnes du tableau et leur contenu
  const columns = useMemo(
    () =>
      stakeholdersDatatableColumns({
        t,
        setOpenDeleteModal: handleOpenDeleteModal,
        setOpenEditModal: handleOpenEditModal,
        setOpenEntityDetailsModal: handleOpenEntityDetailsModal,
        setOpenDuplicateCheckModal: handleOpenDuplicateCheckModal,
        isReadOnly,
        handleDocument,
        handlePreviewDocument,
        fileLoading,
        handleExternalDocumentChange,
        stakeholdersCounter,
        isPPH,
      }),
    [
      handleOpenDeleteModal,
      handleOpenEditModal,
      handleOpenEntityDetailsModal,
      handleOpenDuplicateCheckModal,
      isReadOnly,
      handleDocument,
      handlePreviewDocument,
      fileLoading,
      handleExternalDocumentChange,
      stakeholdersCounter,
      isPPH,
    ]
  );

  const handleCallEReputation = useCallback(async () => {
    if (!folderFile.folderFile?.id) {
      return;
    }

    setLoading(true);

    try {
      await callEReputation({
        variables: {
          folderFileId: folderFile.folderFile.id,
        },
        context: {
          suppressRedirect: true,
        },
      });
      await refetch();
      toast({
        description: t("folder.entities.reloadSuccess"),
      });
    } catch (error) {
      toast({
        description: t("folder.entities.reloadError"),
        variant: "destructive",
      });
      console.error("[useStakeholdersTable::handleCallEReputation] callEReputation failed", error);
    } finally {
      setLoading(false);
    }
  }, [folderFile, callEReputation, setLoading, refetch, toast]);

  return {
    columns,
    data,
    loading: loadingListStakeholders,
    isLegalRepresentative,
    selectedStakeholderForEdit,
    openModalAddBasicStakeholder,
    isReadOnly,
    openUpdateModal,
    setOpenUpdateModal,
    displayReloadButton,
    handleCallEReputation,
    openStakeholderDocumentModal,
    setOpenStakeholderDocumentModal,
    documentToUpload,
    documentToPreview,
    openDocumentPreviewModal,
    handleOpenDocumentPreviewModal,
    handlePreviewDocument,
    isPPH,
    openDuplicateCheckModal,
    setOpenDuplicateCheckModal: handleOpenDuplicateCheckModal,
    selectedStakeholderForDuplicateCheck,
  };
};
